const MODIFIED = 'Modified';
const UNMODIFIED = 'Unmodified';
const DYNAMICALLY_LINKED = 'Dynamically Linked';
const STATICALLY_LINKED = 'Statically Linked';

export const DEFAULT_PACKAGE_LABELS = [
  MODIFIED,
  UNMODIFIED,
  DYNAMICALLY_LINKED,
  STATICALLY_LINKED,
];

/**
 * The values making up the `PackageLabelScope` type.
 *
 * Creating them this way allows us to iterate over them at runtime,
 * while retaining the same functionality as a custom literal in the type checker.
 */
export const PackageLabelScopeValues = ['org', 'project', 'revision'] as const;

/**
 * Valid scopes for `PackageLabel` assignments.
 */
export type PackageLabelScope = typeof PackageLabelScopeValues[number];

/**
 * With org-scoped package labels, no further information is required.
 */
export type OrgPackageLabelScope = {
  scope: 'org';
};

/**
 * With project-scoped package labels, scopeId is assumed to be a project locator.
 */
export type ProjectPackageLabelScope = {
  scope: 'project';
  scopeId: string;
}

/**
 * With revision-scoped package labels, scopeId is assumed to be a revision locator.
 */
export type RevisionPackageLabelScope = {
  scope: 'revision';
  scopeId: string;
}

/**
 * This type represents a union of all possible package label scopes.
 */
export type AnyPackageLabelScope =
  | OrgPackageLabelScope
  | ProjectPackageLabelScope
  | RevisionPackageLabelScope;

/**
 * Represents the subset of PackageLabelInstance and PackageLabelAssignmentInstance
 * fields necessary for rendering label badges on the frontend.
 */
export type PackageLabel = {
  name: string;
  scope: PackageLabelScope;
};

/**
 * Regex for validating package label names.
 *
 * - Must be 1-50 characters long
 * - Must contain only alphanumeric characters, underscores, hyphens, periods, or spaces
 * - Must not contain any other characters
 */
// eslint-disable-next-line no-useless-escape -- This is a valid regex
export const packageLabelNameRegex = /^[a-zA-Z0-9_\-\. ]{1,50}$/;
